package com.tregz.tremenz.io.cloud

import com.tregz.tremenz.io.model.ModelRepository
import com.tregz.tremenz.io.model.ModelStatus
import com.tregz.tremenz.io.model.parseMixDocument
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch

/** The run in flight, if any, and whether a run has already read every collection. Single threaded. */
private var inFlight: Job? = null
private var hasSynced = false

actual class CloudSource actual constructor() {

    actual fun sync(): Job {
        ModelRepository.registerRetry("cloud") { CloudSource().sync() }
        inFlight?.takeIf { it.isActive }?.let { return it }
        if (hasSynced) return Job().also { it.complete() }
        ModelRepository.reportMixes(ModelStatus.LOADING)
        val job = MainScope().launch {
            var isComplete = false
            try {
                // Anonymous sign-in is required by Firestore and Storage security rules
                try {
                    console.log("Firebase signing in anonymously...")
                    CloudFirebase.signInAnonymously()
                    console.log("Firebase signed in anonymously successfully.")
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Throwable) {
                    console.error("Firebase anonymous sign-in failed", e)
                }

                val db = CloudFirebase.firestore
                val storage = CloudFirebase.storage

                // Every collection is attempted; the run is complete only when all of them were read.
                isComplete = true
                for (path in CloudPath.entries.map { it.lowercase() }) {
                    if (!addVideos(db, storage, path)) isComplete = false
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Throwable) {
                console.error("Error in CloudSource.sync", e)
            } finally {
                if (isComplete) hasSynced = true else ModelRepository.reportMixes(ModelStatus.FAILED)
            }
        }
        inFlight = job
        return job
    }

    /** False when the collection could not be read; a document that cannot be mapped is skipped. */
    private suspend fun addVideos(db: dynamic, storage: dynamic, path: String): Boolean {
        try {
            console.log("Fetching collection '$path'...")
            val snapshot = CloudFirebase.documents(db, path)
            val docs = snapshot.docs
            val length = docs.length as Int
            console.log("Collection '$path' returned $length documents.")

            for (i in 0 until length) {
                val doc = docs[i]
                val id = doc.id as String
                val tracks = mutableListOf<String>()
                try {
                    val subSnapshot = CloudFirebase.documents(doc.ref, "list")
                    val listDocs = subSnapshot.docs
                    val listLength = listDocs.length as Int
                    for (j in 0 until listLength) {
                        val item = listDocs[j]
                        tracks.add(trackJson(item.id as String, item.data()))
                    }
                } catch (e: Throwable) {
                    console.error("Error fetching tracks subcollection for doc $id", e)
                }
                val (audio, video) = parseMixDocument(id, path, JSON.stringify(doc.data()), tracks)
                    ?: run {
                        console.warn("Skipping malformed document $id of '$path'")
                        continue
                    }
                val name = video.name

                // Resolve poster URL from Firebase Storage
                var posterUrl: String? = null
                if (storage != null && name.isNotBlank()) {
                    try {
                        val posterPath = "$path/ps_$name.jpg"
                        posterUrl = CloudFirebase.downloadUrl(storage, posterPath)
                        console.log("Resolved poster URL for $posterPath: $posterUrl")
                    } catch (e: Throwable) {
                        console.warn("Storage getDownloadURL failed for $path/ps_$name.jpg", e)
                        posterUrl = null
                    }
                }

                ModelRepository.add(audio, video, posterUrl)
            }
            return true
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            console.error("Error fetching collection $path", e)
            return false
        }
    }

    /** A track document as the JSON the shared mapper reads, with the document id as its `id`. */
    private fun trackJson(id: String, data: dynamic): String {
        val json: dynamic = JSON.parse(JSON.stringify(data))
        json.id = id
        return JSON.stringify(json)
    }
}
